<template>

    <div :class="['form-group', { 'mb-4': !removeMargin }]">

            <label v-if="!noLabel" for="vehicle_id">
                <strong>
                    Vehicle
                    <span v-if="required" class="text-danger">*</span>
                </strong>
            </label>

            <div v-if="vehicles.length === 0" class="alert alert-danger">
                <p class="mb-0">
                  Customer has no Vehicles
                </p>
            </div>

            <v-select
                v-model="model"
                @search="fetchOptions"
                id="vehicle_id"
                placeholder="Select a Vehicle"
                :options="vehicles"
                :clearable="!isDisabled"
                :disabled="isDisabled"
                :get-option-label="getVehicleLabel"
                :loading="!vehicles.length"
            />

            <input type="hidden" name="vehicle_id" :value="model?.id ?? ''">

            <div v-if="errors?.length" >
                <div class="alert alert-danger mt-2" role="alert" v-for="(error, index) in errors" :key="index">
                    {{ error }}
                </div>
            </div>

        </div>

</template>

<script setup>

import { ref, watch, onMounted } from 'vue';
import axios from 'axios';
import debounce from 'lodash/debounce';
import VSelect from 'vue-select';

const props = defineProps({
    old: {
        type: String,
        required: false,
        default: null,
    },
    errors: {
        type: Array,
        required: false,
        default: () => [],
    },
    customer: {
        type: Object,
        default: null,
        required: false
    },
    vehicle: {
        type: Object,
        default: null,
        required: false
    },
    disableWhenPassedVehicle: {
        type: Boolean,
        default: false,
        required: false
    },
    autoSelectSingleVehicle: {
        type: Boolean,
        default: true,
        required: false
    },
    removeMargin: {
        type: Boolean,
        default: false,
        required: false
    },
    required: {
        type: Boolean,
        default: false,
        required: false
    },
    noLabel: {
        type: Boolean,
        default: false,
        required: false
    }
});

const model = defineModel({
    type: Object,
    default: null
});

const vehicles = ref([]);
const isDisabled = ref(false);

const fetchOptions = (search, loading) => {
    if (search.length > 2) {
        debounceFetchOptions(search, loading);
    }
};

const debounceFetchOptions = debounce(async (search, loading) => {
    try {
        loading(true);
        const url = generateVehiclesRoute() + '?search=' + encodeURIComponent(search);
        const { data } = await axios.get(url);
        vehicles.value = Array.isArray(data) ? data : Object.values(data);
    } catch (error) {
        loading(false);
        console.log(error);
    } finally {
        loading(false);
    }
}, 500);

const generateVehiclesRoute = () => {
    return route('api.movements.vehicles.select', props.customer ? { customer: props.customer.id } : {});
};

const getVehicleLabel = (vehicle) => {
    let label = vehicle.reg;
    if ( vehicle?.vehicle_type?.type ) {
        label += ` (${vehicle.vehicle_type.type})`;
    }
    return label;
};

const fetchVehicles = async () => {
    try {
        const { data } = await axios.get(generateVehiclesRoute());
        vehicles.value = Array.isArray(data) ? data : Object.values(data);

        isDisabled.value = vehicles.value.length === 0;

        if (props.old != null) {
            model.value = vehicles.value.find(vehicle => Number(vehicle.id) === Number(props.old));
            return;
        }

        if (props.vehicle) {
            const match = vehicles.value.find(v => v.id === props.vehicle.id);
            if (match) model.value = match;
        }

        if (props.autoSelectSingleVehicle && vehicles.value.length === 1) {
            model.value = vehicles.value[0];
        }
    } catch (error) {
        console.error("Error fetching vehicles:", error);
    }
};

onMounted(() => {
    fetchVehicles();
});

watch(() => props.customer,() => {
    fetchVehicles();
});

watch(() => props.vehicle, () => {
    model.value = props.vehicle;
});

watch(model, (newValue) => {
    if (newValue === null) {
        fetchVehicles();
    }
});

</script>
